/**
 * EvidenceTray
 * Horizontal scrollable strip for unsorted/suggested evidence with AI suggestion badges.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X, ImageIcon } from 'lucide-react';
import { useAcceptSuggestion, useRejectSuggestion } from '@/hooks/useInspectionEvidence';
import type { InspectionEvidence } from '@/hooks/useInspectionEvidence';

interface EvidenceTrayProps {
  evidence: InspectionEvidence[];
  inspectionId: string;
  ticketId: string;
  shopId: string;
  canEdit: boolean;
}

export function EvidenceTray({ evidence, inspectionId, ticketId, shopId, canEdit }: EvidenceTrayProps) {
  const acceptMut = useAcceptSuggestion();
  const rejectMut = useRejectSuggestion();

  if (evidence.length === 0) return null;

  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">
        Unsorted Evidence ({evidence.length})
      </span>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {evidence.map(e => (
          <div key={e.id} className="relative flex-shrink-0 w-20 h-20 rounded-lg border bg-muted/50 overflow-hidden group">
            {e.signedUrl ? (
              <img src={e.signedUrl} alt="Evidence" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
            )}

            {/* AI suggestion badge */}
            {e.status === 'suggested' && e.predicted_item_key && (
              <Badge className="absolute bottom-0 left-0 right-0 text-[9px] rounded-none bg-primary/90 truncate">
                {e.predicted_item_key} ({Math.round((e.predicted_item_confidence ?? 0) * 100)}%)
              </Badge>
            )}

            {/* Accept/Reject buttons for suggestions */}
            {canEdit && e.status === 'suggested' && (
              <div className="absolute top-0 right-0 flex gap-0.5 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 bg-green-500/80 hover:bg-green-500 text-white"
                  onClick={() => acceptMut.mutate({ evidenceId: e.id, inspectionId, ticketId, shopId })}
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 bg-red-500/80 hover:bg-red-500 text-white"
                  onClick={() => rejectMut.mutate({ evidenceId: e.id })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
